import { Directive, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import * as d3 from 'd3';

import tinyColor from 'tinycolor2';

import { Auction } from '@/models/auction-house/auction';

import { ThemeService } from '@/services/theme.service';
import { Web3Service } from '@/services/web3.service';

import { placeholderPunkData } from './placeholderPunkData';
import { GlobalState } from '@/models/auction-house/state';

@Directive({
  standalone: true,
  selector: 'punk-image'
})

export class PunkImageDirective implements OnChanges {

  @Input() auction!: GlobalState['dataState']['activeAuction'] | undefined;
  @Input() animate: boolean = false;
  @Input() dimensions!: { width: number, height: number };
  @Input() addBackground?: boolean;
  @Input() changeBackground: boolean = true;

  // SVG Chart Element
  svg!: d3.Selection<SVGGElement, unknown, null, undefined>;

  // Dimensions
  punkMargin = { top: 0, right: 0, bottom: 0, left: 0 };
  punkWidth!: number;
  punkHeight!: number;

  // Scales & Axis
  xScale!: d3.ScaleBand<string>;
  yScale!: d3.ScaleBand<string>;

  // Chart data
  dataArr: Array<any> = [];

  constructor(
    private el: ElementRef,
    private web3Svc: Web3Service,
    private themeSvc: ThemeService
  ) {}

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!this.svg) await this.buildChart();

    // if (changes['punkId']?.currentValue) this.buildPunk(`${this.punkId}`);

    const auction = changes['auction'];
    // console.log(auction);
    if (
      auction.currentValue?.punkId &&
      auction.currentValue?.punkId !== '-1' &&
      auction?.currentValue?.punkId !== auction?.previousValue?.punkId &&
      auction.currentValue
    ) {
      this.buildPunk(auction.currentValue.punkId);
    }
  }

  async buildChart(): Promise<void> {

    this.punkWidth = this.dimensions.width - this.punkMargin.left - this.punkMargin.right;
    this.punkHeight = this.dimensions.height - this.punkMargin.top - this.punkMargin.bottom;

    const element = this.el.nativeElement as HTMLElement;
    element.style.width = this.punkWidth + 'px';
    element.style.height = this.punkHeight + 'px';

    // Create the SVG
    this.svg = d3.select(this.el.nativeElement)
      .append('svg')
        .style('display', 'block')
        .attr('preserveAspectRatio', 'xMinYMin meet')
        .attr('viewBox', `0 0 ${this.punkHeight} ${this.punkWidth}`)
        .attr('width', '100%')
        // .attr('height', '100%')
      .append('g')
        .attr('transform', `translate(${this.punkMargin.left}, ${this.punkMargin.top})`);

    const x = [...Array(24).keys()].map((i) => `${i}`);
    const y = [...Array(24).keys()].map((i) => `${i}`).reverse();

    // Build X scales and axis:
    this.xScale = d3.scaleBand()
      .range([ 0, this.punkWidth ])
      .domain(x)
      .padding(0);

    this.svg.append('g')
      .attr('transform', `translate(0, ${this.punkHeight})`)
      .call(d3.axisBottom(this.xScale))
      .call((g: any) => g.select('.domain').remove());

    // Build Y scales and axis:
    this.yScale = d3.scaleBand()
      .range([ this.punkHeight, 0 ])
      .domain(y)
      .padding(0);

    this.svg.append('g')
      .call(d3.axisLeft(this.yScale))
      .call((g: any) => g.select('.domain').remove());

    this.svg.selectAll('text')
      .remove()

    this.buildPunk();
  }

  async buildPunk(punkId?: string): Promise<void> {

    let punkData = punkId ? await this.web3Svc.getPunkImage(punkId) ?? placeholderPunkData : placeholderPunkData;

    const punkArr = punkData?.replace('0x', '').match(/.{1,8}/g) as string[];
    this.dataArr = punkArr.map((res, i, arr) => ({ x: `${i % 24}`, y: `${Math.floor(i / 24)}`, value: res }));

    this.svg.selectAll('rect')
      .data(this.dataArr)
        .join('rect')
          .style('fill', '#00000000')
          .attr('shape-rendering', 'crispEdges')
          .attr('width', this.xScale.bandwidth())
          .attr('height', this.yScale.bandwidth())
          .attr('x', (d: any) => this.xScale(d.x) as any)
          .attr('y', (d: any) => this.yScale(d.y) as any);

    if (!this.animate) {
      this.svg.selectAll('rect')
        .data(this.dataArr)
        .style('fill', (d) => `#${d.value}`)
    } else {
      this.svg.selectAll('rect')
        .data(this.dataArr)
        .transition()
          .style('fill', (d) => `#${d.value}`)
          .delay((d, i) => Math.floor(Math.random() * this.dataArr.length));
    }


    if (this.changeBackground) {
      const colorGroups: any = {};
      this.dataArr.map((res: any) => {
        if (!colorGroups[res.value]) colorGroups[res.value] = 1;
        colorGroups[res.value]++;
      });

      const hslColors = Object.keys(colorGroups).map((key: any) => tinyColor(key).toHsl());
      const brightest = hslColors.sort((a: any, b: any) => (`${b.s}`.localeCompare(`${a.s}`) || b.l - a.l))[2];
      const rgb = tinyColor(brightest).toRgb();
      this.themeSvc.setBackgroundColor(rgb);

      if (this.addBackground) {
        const element = this.el.nativeElement as HTMLElement;
        element.style.backgroundColor = tinyColor(brightest).setAlpha(.15).toRgbString();
      }
    }
  }

}
